import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function RegisterSchool() {
  const nav = useNavigate();
  const { user, refresh, loading } = useAuth();
  const [form, setForm] = useState({ school_name: "", contact_person: "", phone: "", address: "" });
  const [saving, setSaving] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const googleLogin = () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${API}/api/auth/google/login?next=${encodeURIComponent("/register-school")}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/school-from-google", form);
      await refresh();
      toast.success("School profile created. Next: upload your student list.");
      nav("/school");
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <PublicLayout><div className="p-20 text-center">Loading…</div></PublicLayout>;
  }

  return (
    <PublicLayout>
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-14">
        <div className="bb-overline text-[var(--bb-terracotta)] mb-3">FOR SCHOOLS</div>
        <h1 className="font-serif text-5xl md:text-6xl text-[var(--bb-forest)] tracking-tight leading-[1.05]">Register your school.</h1>
        <p className="mt-5 text-[var(--bb-ink)]/75 max-w-xl">Step 1: Sign in with Google. Step 2: Tell us about your school. Step 3: Upload a simple student list — we'll take it from there.</p>

        {!user ? (
          <div className="mt-10 bb-card p-10 text-center">
            <div className="bb-overline text-[var(--bb-muted)] mb-3">STEP 1 · SIGN IN</div>
            <p className="text-[var(--bb-ink)]/80 mb-6 max-w-md mx-auto">Schools sign in with Google. You'll fill your school profile on the next step.</p>
            <button onClick={googleLogin} className="bb-btn-primary inline-flex items-center justify-center gap-3" data-testid="register-google">
              <svg width="14" height="14" viewBox="0 0 24 24"><path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/></svg>
              Continue with Google
            </button>
          </div>
        ) : user.school_id ? (
          <div className="mt-10 bb-card p-10 text-center">
            <div className="bb-overline text-[var(--bb-muted)] mb-3">YOU ARE ALREADY REGISTERED</div>
            <Link to="/school" className="bb-btn-primary inline-flex items-center gap-2" data-testid="goto-school">Go to school dashboard <ArrowRight size={14}/></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 bb-card p-8 space-y-6 max-w-2xl" data-testid="register-school-form">
            <div className="text-xs text-[var(--bb-muted)]">Signed in as <b>{user.email}</b></div>
            <Row label="School name"><input required value={form.school_name} onChange={change("school_name")} className="field" data-testid="reg-school-name"/></Row>
            <Row label="Contact person"><input required value={form.contact_person} onChange={change("contact_person")} className="field" data-testid="reg-contact"/></Row>
            <Row label="Phone"><input required value={form.phone} onChange={change("phone")} className="field" data-testid="reg-phone"/></Row>
            <Row label="Address"><input required value={form.address} onChange={change("address")} className="field" data-testid="reg-address"/></Row>
            <style>{`.field{width:100%;background:transparent;border-bottom:1px solid var(--bb-border);padding:8px 0;outline:none;font-size:15px;transition:border-color .2s} .field:focus{border-color:var(--bb-terracotta)}`}</style>
            <div className="flex items-center gap-4 pt-4">
              <button type="submit" disabled={saving} className="bb-btn-primary" data-testid="reg-submit">{saving ? "Saving…" : "Create school profile"}</button>
              <Link to="/" className="text-sm text-[var(--bb-muted)] hover:text-[var(--bb-terracotta)]">Not now</Link>
            </div>
          </form>
        )}
      </section>
    </PublicLayout>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <label className="bb-overline text-[var(--bb-muted)]">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
